import { useCallback, useMemo, useState } from 'react'

import { Trans, useTranslation } from 'react-i18next'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import UnsubscribeProfileModal from 'components/UI/Modal/profileModals/UnsubscribeProfileModal/UnsubscribeProfileModal'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import styles from 'features/NotificationCard/_NotificationCard.module.scss'
import { toFixedNumber } from 'helpers/dataTransformations'
import { setSubscriptionRenewal } from 'services/endpoints/settings/general/subscription'

const SubscriptionPriceChangedNotification = ({ data }: { data: any }) => {
  const { t } = useTranslation()

  const [disableBtn, setDisableBtn] = useState(false)
  const [openRenewalModal, setOpenRenewalModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const subscriptionId = data?.subscription?.id

  const sender = useMemo(() => {
    const s = data?.sender ?? {}

    return {
      id: s?.id ?? null,
      name: s?.name ?? s?.display_name ?? '',
      username: s?.username ?? s?.handle ?? '',
      avatar: s?.avatar ?? s?.avatar_url ?? s?.profile_pic ?? null,
    }
  }, [data])

  const oldPrice = useMemo(
    () => toFixedNumber(data?.subscription?.old_price),
    [data]
  )

  const newPrice = useMemo(
    () => toFixedNumber(data?.subscription?.new_price),
    [data]
  )

  const rebill = useMemo(() => {
    const value = data?.subscription?.rebill

    if (value === undefined || value === null) {
      return null
    }

    return Number(value)
  }, [data])

const ctaPrice = useMemo(() => {
  const raw =
    data?.subscription?.price ??
    data?.subscription?.new_price

  const value = Number(raw)

  if (Number.isNaN(value)) {
    return '0'
  }

  return Number.isInteger(value)
    ? String(value)
    : value.toFixed(2)
}, [data])

  const isPriceIncrease = useMemo(() => {
    return newPrice > oldPrice
  }, [newPrice, oldPrice])

  const shouldShowEnableRenewal = useMemo(() => {
    return !!subscriptionId && rebill === 0 && isPriceIncrease
  }, [subscriptionId, rebill, isPriceIncrease])

  const expiryAt = useMemo(() => {
    return (
      data?.subscription?.expiry_at ??
      data?.subscription?.expires_at ??
      data?.subscription?.renewal_at ??
      null
    )
  }, [data])

  const openConfirmModal = useCallback(() => {
    if (disableBtn) return

    setOpenRenewalModal(true)
  }, [disableBtn])

  const confirmEnableRenewal = useCallback(async () => {
    if (isSubmitting) return

    if (!subscriptionId) {
      addToast('error', t('error:renewalSwitchOnError'))
      return
    }

    try {
      setIsSubmitting(true)
      setDisableBtn(true)

      await setSubscriptionRenewal(subscriptionId)

      setOpenRenewalModal(false)
    } catch (error) {
      setDisableBtn(false)
      addToast('error', t('error:renewalSwitchOnError'))
      console.error('renewal error: ', error)
    } finally {
      setIsSubmitting(false)
    }
  }, [isSubmitting, subscriptionId, t])

  return (
    <>
      <Trans i18nKey={'notifications:subscriptionPriceChange'}>
        {{ name: sender?.name }} changed {{ oldPrice }} to {{ newPrice }}
      </Trans>

      {shouldShowEnableRenewal && (
        <>
          <div className={styles.btnsRow}>
            <Button
              text={
                <p>
                  Enable Renewal{' '}
<span className={styles.contentText}>
  ${ctaPrice}
</span>
<span className={styles.perMonth}>/mo</span>
                </p>
              }
              customClass="notificationPriceButton"
              width="fit"
              color="blue"
              padding="1-15"
              disabled={disableBtn}
              clickFn={openConfirmModal}
            />
          </div>

          <ModalWrapper
            open={openRenewalModal}
            setOpen={setOpenRenewalModal}
            backgroundClickClose={!isSubmitting}
            hasCloseButton={false}
          >
            <UnsubscribeProfileModal
              avatar={sender.avatar}
              displayName={sender.name}
              username={sender.username}
              setOpen={setOpenRenewalModal}
              rebill={0}
              expiry_at={expiryAt}
              subscribeFn={confirmEnableRenewal}
              isLoading={isSubmitting}
            />
          </ModalWrapper>
        </>
      )}
    </>
  )
}

export default SubscriptionPriceChangedNotification